import { IconAlertTriangle, IconFileInvoice, IconReceiptOff } from "@tabler/icons-react";
import dayjs from "dayjs";
import type { ReactNode } from "react";
import { Button, Group, Skeleton, Stack, Table, Tbody, Td, Text, Th, Thead, Tr, TruncatedTooltip } from "@/components/ui";
import { AccionesDescargaFactura } from "@/features/mis-facturas/components/AccionesDescargaFactura";
import { EstatusFacturaBadge } from "@/features/mis-facturas/components/EstatusFacturaBadge";
import { useFacturasDeSolicitud } from "@/features/mis-facturas/hooks/use-facturas-de-solicitud";
import type { Factura } from "@/features/mis-facturas/services/facturas.api";
import { serieFolio } from "@/features/mis-facturas/utils/facturas-de-solicitud";
import { extractApiMessage } from "@/shared/api/extract-message";
import { formatearMonto } from "@/shared/format/money";

/** Columnas de la sub-tabla · pesos relativos (se normalizan a %). */
const COLS: { label: string; w: number; align?: "right" | "center" }[] = [
  { label: "UUID", w: 250 },
  { label: "Serie-Folio", w: 90 },
  { label: "Emisor", w: 200 },
  { label: "Fecha timbrado", w: 120 },
  { label: "Total", w: 120, align: "right" },
  { label: "Estatus", w: 100 },
  { label: "Acciones", w: 120, align: "center" },
];
const TOTAL_PESO = COLS.reduce((acc, c) => acc + c.w, 0);

function fechaTimbrado(iso: string | null): string {
  return iso ? dayjs(iso).format("DD/MM/YYYY HH:mm") : "";
}

/**
 * Fila de DETALLE de «Mis solicitudes de pago» (patrón "expandable row" de IBM
 * Carbon): ocupa todo el ancho de la tabla (`colSpan`) y muestra las facturas de
 * la solicitud con su estatus de validación y las descargas. Se monta SOLO al
 * expandir → la petición sale hasta entonces (carga perezosa).
 */
export function FacturasDeSolicitudRow({
  id,
  solicitudId,
  folio,
  colSpan,
}: {
  /** Id del `<tr>` · lo referencia el `aria-controls` del botón de expandir. */
  id: string;
  solicitudId: string;
  folio: string;
  colSpan: number;
}) {
  return (
    // Fila de detalle con el MISMO tinte gris pizarra que la fila expandida (SolicitudRow) para
    // que se lean como un bloque; el panel lleva borde izquierdo slate + sombra para que la tabla
    // secundaria no se pierda entre las filas de la tabla principal (tonos neutros que combinan
    // con las tablas grises · el naranja de marca chocaba). Tintes por color-mix (no un -50
    // sólido) para que funcionen igual en modo oscuro.
    <Tr
      id={id}
      className="bg-[color-mix(in_oklab,var(--color-slate-500)_8%,transparent)] hover:bg-[color-mix(in_oklab,var(--color-slate-500)_8%,transparent)]"
    >
      <Td colSpan={colSpan} className="!px-4 !pb-4 !pt-2">
        <section
          aria-label={`Facturas de la solicitud ${folio}`}
          className="overflow-hidden rounded-md border border-[color-mix(in_oklab,var(--color-slate-500)_35%,var(--color-border))] border-l-4 border-l-slate-500 dark:border-l-slate-400 bg-card shadow-md"
        >
          <FacturasDeSolicitudContenido solicitudId={solicitudId} folio={folio} />
        </section>
      </Td>
    </Tr>
  );
}

function FacturasDeSolicitudContenido({ solicitudId, folio }: { solicitudId: string; folio: string }) {
  const { data, isPending, isError, error, refetch, isFetching } = useFacturasDeSolicitud(solicitudId);
  return (
    <>
      <EncabezadoPanel folio={folio} total={data?.length} />
      <CuerpoPanel {...{ data, isPending, isError, error, refetch, isFetching }} />
    </>
  );
}

/** Encabezado del panel · identifica de qué solicitud son las facturas y cuántas hay. */
function EncabezadoPanel({ folio, total }: { folio: string; total?: number }) {
  return (
    <Group
      gap={8}
      className="border-b border-[color-mix(in_oklab,var(--color-slate-500)_25%,var(--color-border))] bg-[color-mix(in_oklab,var(--color-slate-500)_14%,var(--color-card))] px-3 py-2"
    >
      <IconFileInvoice size={16} stroke={1.9} className="text-slate-600 dark:text-slate-300" />
      <Text size="xs" fw={700} className="uppercase tracking-wide text-slate-700 dark:text-slate-200">
        Facturas de la solicitud {folio}
      </Text>
      {total !== undefined && total > 0 && (
        <span className="rounded-full bg-slate-600 px-2 py-0.5 text-[11px] font-semibold leading-none text-white dark:bg-slate-500">
          {total}
        </span>
      )}
    </Group>
  );
}

function CuerpoPanel({
  data,
  isPending,
  isError,
  error,
  refetch,
  isFetching,
}: Pick<ReturnType<typeof useFacturasDeSolicitud>, "data" | "isPending" | "isError" | "error" | "refetch" | "isFetching">) {

  if (isPending) return <SubTabla>{[0, 1].map((i) => <FilaSkeleton key={i} />)}</SubTabla>;
  if (isError)
    return (
      <EstadoFacturas icon={<IconAlertTriangle size={18} className="text-peligro-600" />}>
        <Text size="sm" className="normal-case">
          No se pudieron cargar las facturas: {extractApiMessage(error, "error desconocido")}
        </Text>
        <Button size="xs" variant="default" loading={isFetching} onClick={() => void refetch()}>
          Reintentar
        </Button>
      </EstadoFacturas>
    );
  if (!data || data.length === 0)
    return (
      <EstadoFacturas icon={<IconReceiptOff size={18} className="text-muted-foreground" />}>
        <Text size="sm" c="dimmed" className="normal-case">
          Esta solicitud no tiene facturas cargadas.
        </Text>
      </EstadoFacturas>
    );

  return <SubTabla>{data!.map((f) => <FilaFactura key={f.id} f={f} />)}</SubTabla>;
}

function SubTabla({ children }: { children: ReactNode }) {
  return (
    <Table layout="fixed" verticalSpacing="sm" horizontalSpacing="sm">
      <Thead className="bg-muted/60">
        <Tr>
          {COLS.map((c) => (
            <Th key={c.label} align={c.align} style={{ width: `${((c.w / TOTAL_PESO) * 100).toFixed(2)}%` }}>
              <Text size="xs" fw={600} className="uppercase leading-tight text-ink-700 dark:text-ink-200">
                {c.label}
              </Text>
            </Th>
          ))}
        </Tr>
      </Thead>
      <Tbody>{children}</Tbody>
    </Table>
  );
}

function FilaFactura({ f }: { f: Factura }) {
  return (
    <Tr>
      <Td>
        {/* UUID fiscal en MAYÚSCULAS (también al copiarlo) y resaltado · mono, parte (`break-all`) para caber. */}
        <Text size="sm" fw={600} ff="mono" className="break-all leading-tight text-ink-900 dark:text-ink-50">
          {f.uuid.toUpperCase()}
        </Text>
      </Td>
      <Td>
        <Text size="sm">{serieFolio(f)}</Text>
      </Td>
      <Td>
        <TruncatedTooltip label={`${f.nombreEmisor} · ${f.rfcEmisor}`} disabled={!f.nombreEmisor}>
          <Stack gap={0} className="min-w-0">
            <Text size="sm" fw={600} truncate>
              {f.nombreEmisor || ""}
            </Text>
            <Text size="xs" c="dimmed" truncate>
              {f.rfcEmisor}
            </Text>
          </Stack>
        </TruncatedTooltip>
      </Td>
      <Td>
        <Text size="sm">{fechaTimbrado(f.fechaTimbrado)}</Text>
      </Td>
      <Td align="right">
        <Stack gap={0} className="items-end">
          <Text size="sm" fw={700} className="whitespace-nowrap">
            {formatearMonto(f.total, f.moneda, { minimumFractionDigits: 2, currencyDisplay: "narrowSymbol" })}
          </Text>
          <Text size="xs" c="dimmed" className="leading-tight">
            {f.moneda}
          </Text>
        </Stack>
      </Td>
      <Td>
        <EstatusFacturaBadge estatus={f.estatus} />
      </Td>
      <Td align="center">
        <AccionesDescargaFactura factura={f} />
      </Td>
    </Tr>
  );
}

function FilaSkeleton() {
  return (
    <Tr aria-hidden="true">
      {COLS.map((c) => (
        <Td key={c.label}>
          <Skeleton height={14} width={c.label === "Acciones" ? 96 : "80%"} className={c.label === "Acciones" ? "mx-auto" : undefined} />
        </Td>
      ))}
    </Tr>
  );
}

function EstadoFacturas({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <Group gap="sm" justify="center" className="px-4 py-5" role="status">
      {icon}
      {children}
    </Group>
  );
}
